import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Star, ShieldCheck, BadgeCheck, Store, Headset, MessageCircle,
  CheckCircle2, Gamepad2, Users, Baby, Tag, Barcode, ChevronLeft,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { useStore } from "../context/StoreContext";
import { getDefaultDescription, getHighlights } from "../products";
import { fetchProduct, fetchProducts, resolveImageUrl } from "../api";
import { waLink, WhatsAppIcon } from "../components/EnquiryWidget";
import { LOGO_URL } from "../mock";
import { Gift } from "lucide-react";

const NAVY = "#1B1F5C";

const CATEGORY_META = {
  "toys-games": { label: "Toys & Games", path: "/toys-games" },
  "learning-education": { label: "Learning & Education", path: "/learning-education" },
  gifts: { label: "Gifts", path: "/gifts" },
};

const Stars = ({ rating, size = 15 }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= Math.round(rating) ? "text-[#F79433] fill-[#F79433]" : "text-gray-300 fill-gray-300"} />
    ))}
  </span>
);

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { wishlist, cart, toggleWishlist, addToCart } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchProduct(id)
      .then((data) => {
        if (!active) return;
        const meta = CATEGORY_META[data.category] || CATEGORY_META["toys-games"];
        const enriched = { ...data, category: meta.label, categoryPath: meta.path };
        setProduct(enriched);
        return fetchProducts({ category: data.category, limit: 8 }).then((list) => {
          if (!active) return;
          const seen = new Set([enriched.name]);
          const rel = [];
          for (const p of list) {
            if (String(p.id) !== String(id) && !seen.has(p.name)) {
              seen.add(p.name);
              rel.push(p);
            }
            if (rel.length >= 4) break;
          }
          setRelated(rel);
        });
      })
      .catch(() => active && setProduct(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="bg-white min-h-screen">
        <Header cartCount={cart.length} wishlistCount={wishlist.length} />
        <div className="max-w-7xl mx-auto px-4 mt-8 grid lg:grid-cols-2 gap-10">
          <div className="rounded-3xl bg-gray-100 animate-pulse h-[440px]" />
          <div className="space-y-4">
            <div className="h-8 w-3/4 rounded bg-gray-100 animate-pulse" />
            <div className="h-5 w-1/3 rounded bg-gray-100 animate-pulse" />
            <div className="h-24 w-full rounded bg-gray-100 animate-pulse" />
            <div className="h-12 w-1/2 rounded-full bg-gray-100 animate-pulse" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-white min-h-screen">
        <Header cartCount={cart.length} wishlistCount={wishlist.length} />
        <div className="max-w-7xl mx-auto px-4 py-24 text-center">
          <p className="font-display font-extrabold text-2xl" style={{ color: NAVY }}>Product not found</p>
          <button onClick={() => navigate("/")} className="mt-5 px-6 h-11 rounded-full text-white text-[12px] font-extrabold tracking-widest" style={{ backgroundColor: NAVY }}>
            BACK TO HOME
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const specs = [
    product.sku && { icon: Barcode, label: "SKU", value: product.sku },
    product.players && { icon: Users, label: "Players", value: product.players },
    { icon: Baby, label: "Age Group", value: product.ageGroup ? `${product.ageGroup} Years` : product.age || "3+ Years" },
    product.brand && { icon: Tag, label: "Brand", value: product.brand },
  ].filter(Boolean);

  return (
    <div className="bg-white min-h-screen">
      <Header cartCount={cart.length} wishlistCount={wishlist.length} />
      <main>
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 pt-4 text-left">
          <p className="text-[12px] font-bold" style={{ color: NAVY }}>
            <Link to="/" className="hover:text-[#35C3C1] transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">&gt;</span>
            <Link to={product.categoryPath} className="hover:text-[#35C3C1] transition-colors">{product.category}</Link>
            <span className="mx-2 text-gray-400">&gt;</span>
            <span className="text-gray-500">{product.name}</span>
          </p>
        </div>

        <section className="max-w-7xl mx-auto px-4 mt-4 grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image */}
          <div className="relative bg-[#F7F7FA] rounded-3xl border border-gray-100 p-8 md:p-12 flex items-center justify-center min-h-[340px] md:min-h-[440px]">
            {product.inStock === false && (
              <span className="absolute top-5 left-5 z-10 text-white text-[12px] font-extrabold px-3 py-1 rounded-full bg-gray-500">
                OUT OF STOCK
              </span>
            )}
            {product.giftCard ? (
              <div className="relative w-[80%] max-w-[380px] aspect-[16/10] rounded-2xl shadow-xl flex flex-col items-center justify-center gap-2" style={{ backgroundColor: NAVY }}>
                <Gift size={30} className="text-[#FCD44E] absolute top-4 right-5" />
                <img src={LOGO_URL} alt="Adhira & Appa Toys" className="h-20 w-auto bg-white rounded-xl px-3 py-1.5" />
                <span className="text-white text-[13px] font-extrabold tracking-[0.3em] mt-1">GIFT CARD</span>
              </div>
            ) : (
              <img src={resolveImageUrl(product.image)} alt={product.name} className="max-h-[300px] md:max-h-[400px] w-auto object-contain mix-blend-multiply" />
            )}
          </div>

          {/* Details */}
          <div className="text-left">
            <p className="text-[11px] font-extrabold tracking-[0.18em] text-[#35C3C1] uppercase">{product.category}</p>
            <h1 className="mt-1.5 font-display font-extrabold text-2xl md:text-[32px] leading-tight" style={{ color: NAVY }}>
              {product.name}
            </h1>
            <div className="mt-2.5 flex items-center gap-2">
              <Stars rating={product.rating} />
              <span className="text-[13px] font-extrabold" style={{ color: NAVY }}>{product.rating}</span>
              <span className="text-[12.5px] font-semibold text-gray-400">({product.reviews} reviews)</span>
            </div>

            {/* Description */}
            <p className="mt-5 text-[14px] font-medium text-[#5A5A75] leading-relaxed max-w-xl">
              {getDefaultDescription(product)}
            </p>

            {/* Specs */}
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
              {specs.map((s) => (
                <div key={s.label} className="bg-[#F7F7FA] rounded-xl px-3.5 py-3 flex flex-col items-start gap-1.5">
                  <s.icon size={17} className="text-[#35C3C1]" strokeWidth={1.8} />
                  <p className="text-[10.5px] font-bold text-gray-400 uppercase tracking-wide">{s.label}</p>
                  <p className="text-[12.5px] font-extrabold leading-tight" style={{ color: NAVY }}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Enquiry actions */}
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("open-enquiry"))}
                data-testid="product-enquire-btn"
                className="inline-flex items-center gap-2 px-8 h-12 rounded-full text-white text-[12.5px] font-extrabold tracking-widest shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-transform duration-200"
                style={{ backgroundColor: NAVY }}
              >
                <MessageCircle size={17} /> ENQUIRE NOW
              </button>
              <a
                href={waLink(`Hi! I'm interested in "${product.name}" from Adhira & Appa Toys. Please share more details.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-whatsapp-btn"
                className="inline-flex items-center gap-2 px-7 h-12 rounded-full text-white text-[12.5px] font-extrabold tracking-widest bg-[#25D366] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-transform duration-200"
              >
                <WhatsAppIcon size={17} /> WHATSAPP
              </a>
            </div>

            {/* Trust row */}
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
              {[
                { icon: ShieldCheck, text: "100% Safe & Non-Toxic" },
                { icon: BadgeCheck, text: "Trusted Global Brands" },
                { icon: Store, text: "Visit Our Store" },
                { icon: Headset, text: "Quick Enquiry Support" },
              ].map((t) => (
                <div key={t.text} className="flex items-center gap-2">
                  <span className="h-8 w-8 rounded-full bg-[#EEF0FA] flex items-center justify-center shrink-0">
                    <t.icon size={14} style={{ color: NAVY }} strokeWidth={1.8} />
                  </span>
                  <p className="text-[10.5px] font-bold leading-tight" style={{ color: NAVY }}>{t.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to Play / Highlights */}
        <section className="max-w-7xl mx-auto px-4 mt-12 grid md:grid-cols-2 gap-6">
          {product.howToPlay ? (
            <div className="rounded-3xl p-7 md:p-8 text-left" style={{ backgroundColor: "#FBF3E4" }}>
              <div className="flex items-center gap-2.5">
                <span className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: NAVY }}>
                  <Gamepad2 size={18} className="text-[#FCD44E]" />
                </span>
                <h2 className="font-display font-extrabold text-xl" style={{ color: NAVY }}>How to Play</h2>
              </div>
              <ol className="mt-5 space-y-3.5">
                {product.howToPlay.map((step, i) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="h-6 w-6 rounded-full bg-[#35C3C1] text-white text-[11px] font-extrabold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <p className="text-[13.5px] font-bold leading-relaxed" style={{ color: NAVY }}>{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <div className="rounded-3xl p-7 md:p-8 text-left" style={{ backgroundColor: "#FBF3E4" }}>
              <div className="flex items-center gap-2.5">
                <span className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: NAVY }}>
                  <Star size={17} className="text-[#FCD44E] fill-[#FCD44E]" />
                </span>
                <h2 className="font-display font-extrabold text-xl" style={{ color: NAVY }}>Highlights</h2>
              </div>
              <ul className="mt-5 space-y-3.5">
                {getHighlights(product).map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <CheckCircle2 size={17} className="text-[#35C3C1] shrink-0 mt-0.5" />
                    <p className="text-[13.5px] font-bold leading-relaxed" style={{ color: NAVY }}>{h}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Product details card */}
          <div className="rounded-3xl p-7 md:p-8 text-left border border-gray-100 shadow-sm">
            <h2 className="font-display font-extrabold text-xl" style={{ color: NAVY }}>Product Details</h2>
            <div className="mt-5 divide-y divide-gray-100">
              {[
                product.sku && ["SKU", product.sku],
                ["Brand", product.brand || "Adhira & Appa Toys"],
                ["Recommended Age", product.ageGroup ? `${product.ageGroup} Years` : product.age || "3+ Years"],
                product.players && ["Number of Players", product.players],
                ["Category", product.category],
                ["Safety", "100% child-safe, non-toxic materials"],
                ["Country of Origin", "India"],
              ]
                .filter(Boolean)
                .map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-4 py-3">
                    <p className="text-[12.5px] font-bold text-gray-400">{k}</p>
                    <p className="text-[12.5px] font-extrabold text-right" style={{ color: NAVY }}>{v}</p>
                  </div>
                ))}
            </div>
          </div>
        </section>

        {/* Related products */}
        {related.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 mt-14 mb-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-extrabold text-2xl text-left" style={{ color: NAVY }}>You May Also Like</h2>
              <Link to={product.categoryPath} className="inline-flex items-center gap-1 text-[12px] font-extrabold text-[#35C3C1] hover:underline">
                <ChevronLeft size={14} className="rotate-180" /> View All
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} wishlist={wishlist} toggleWishlist={toggleWishlist} addToCart={addToCart} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;
